import {
  Home,
  LogOut,
  Activity,
  Stethoscope,
  Users,
  Brain,
  Settings,
  FileText,
  Pill,
  ClipboardList,
  Thermometer,
  Eye,
  LayoutDashboard,
  AlertTriangle,
  FileCheck,
  ListOrdered,
  Building2,
  Mail,
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/molecules/sidebar";
import { Button } from "@/components/atoms/button";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageProvider";
import { useIntl, FormattedMessage } from "react-intl";
import { Logo } from "@/components/atoms/Logo";
import { cn } from "@/lib/utils";

export function AppSidebar() {
  const { state } = useSidebar();
  const { userRole, signOut } = useAuth();
  const { isRTL } = useLanguage();
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const isActiveRoute = (url: string) => {
    if (url === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    return location.pathname.startsWith(url);
  };

  const collapsed = state === "collapsed";

  const getMenuItems = () => {
    if (userRole === "admin") {
      return [
        {
          title: intl.formatMessage({
            id: "S+ktJ1",
            defaultMessage: "Overview",
            description: "Sidebar nav item",
          }),
          url: "/dashboard",
          icon: LayoutDashboard,
        },
        {
          title: intl.formatMessage({
            id: "F0h2Ut",
            defaultMessage: "AI Diagnostics",
            description: "Sidebar nav item",
          }),
          url: "/dashboard/admin/ai-diagnostics",
          icon: Brain,
        },
        {
          title: intl.formatMessage({
            id: "V46trq",
            defaultMessage: "Emergency Alerts",
            description: "Sidebar nav item",
          }),
          url: "/dashboard/emergency-alerts",
          icon: AlertTriangle,
        },
        {
          title: intl.formatMessage({
            id: '2WG3qL',
            defaultMessage: "Members",
            description: "Sidebar nav item",
          }),
          url: "/dashboard/members",
          icon: Users,
        },
        {
          title: intl.formatMessage({
            id: "ZMhg38",
            defaultMessage: "Settings",
            description: "Sidebar nav item",
          }),
          url: "/dashboard/admin/settings",
          icon: Settings,
        },
        {
          title: intl.formatMessage({
            id: 'gf+Ifs',
            defaultMessage: "Invitations",
            description: "Sidebar nav item",
          }),
          url: "/dashboard/admin/invitations",
          icon: Mail,
        },
        {
          title: intl.formatMessage({
            id: 'PHEn1F',
            defaultMessage: "Hospital settings",
            description: "Sidebar nav item",
          }),
          url: "/dashboard/admin/tenant-settings",
          icon: Building2,
        },
      ];
    }
    if (userRole === "doctor") {
      return [
        {
          title: intl.formatMessage({
            id: "icbxSq",
            defaultMessage: "Dashboard",
            description: "Sidebar nav item",
          }),
          url: "/dashboard",
          icon: Home,
        },
        {
          title: intl.formatMessage({
            id: "eVJISQ",
            defaultMessage: "Medical",
            description: "Sidebar nav item",
          }),
          url: "/dashboard/medical",
          icon: Stethoscope,
        },
        {
          title: intl.formatMessage({
            id: "nzTnjj",
            defaultMessage: "Case Review Queue",
            description: "Sidebar nav item",
          }),
          url: "/dashboard/doctor/case-review",
          icon: FileCheck,
        },
        {
          title: intl.formatMessage({
            id: "oyfDz2",
            defaultMessage: "Patient Queue",
            description: "Sidebar nav item",
          }),
          url: "/dashboard/doctor/patient-queue",
          icon: ListOrdered,
        },
        {
          title: intl.formatMessage({
            id: "zUCgQN",
            defaultMessage: "Prescriptions",
            description: "Sidebar nav item",
          }),
          url: "/dashboard/doctor/prescriptions",
          icon: Pill,
        },
        {
          title: intl.formatMessage({
            id: "V46trq",
            defaultMessage: "Emergency Alerts",
            description: "Sidebar nav item",
          }),
          url: "/dashboard/emergency-alerts",
          icon: AlertTriangle,
        },
      ];
    }
    return [
      {
        title: intl.formatMessage({
          id: "icbxSq",
          defaultMessage: "Dashboard",
          description: "Sidebar nav item",
        }),
        url: "/dashboard",
        icon: Home,
      },
      {
        title: intl.formatMessage({
          id: "+k7T4F",
          defaultMessage: "Nurse Dashboard",
          description: "Sidebar nav item",
        }),
        url: "/dashboard/nurse/dashboard",
        icon: ClipboardList,
      },
      {
        title: intl.formatMessage({
          id: "w69TfL",
          defaultMessage: "Vitals",
          description: "Sidebar nav item",
        }),
        url: "/dashboard/nurse/vitals",
        icon: Thermometer,
      },
      {
        title: intl.formatMessage({
          id: "SPKJBz",
          defaultMessage: "Nurse Notes",
          description: "Sidebar nav item",
        }),
        url: "/dashboard/nurse/notes",
        icon: FileText,
      },
      {
        title: intl.formatMessage({
          id: "ltHHMn",
          defaultMessage: "Patient Status",
          description: "Sidebar nav item",
        }),
        url: "/dashboard/nurse/patient",
        icon: Eye,
      },
      {
        title: intl.formatMessage({
          id: "qCWHQH",
          defaultMessage: "Analysis",
          description: "Sidebar nav item",
        }),
        url: "/dashboard/analysis",
        icon: Activity,
      },
    ];
  };

  const menuItems = getMenuItems();

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <Sidebar
      className={collapsed ? "w-28" : "w-64"}
      collapsible="icon"
      side={isRTL ? "right" : "left"}
    >
      <SidebarHeader className={`border-b border-sidebar-border ${collapsed ? "p-2" : "p-4"}`}>
        {!collapsed ? (
          <Logo size="md" showText={true} />
        ) : (
          <div className="flex items-center justify-center w-full">
            <Logo size="md" variant="icon" />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>
            <FormattedMessage
              id="gXGrrh"
              defaultMessage="Navigation"
              description="Sidebar group label"
            />
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActiveRoute(item.url)}
                    tooltip={collapsed ? item.title : undefined}
                  >
                    <NavLink to={item.url}>
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <Button
          variant="ghost"
          className={`w-full text-sidebar-foreground hover:bg-sidebar-accent/50 ${collapsed ? "justify-center" : isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
          onClick={handleSignOut}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && (
            <span className={isRTL ? "mr-2" : "ml-2"}>
              <FormattedMessage
                id="+Qwnr0"
                defaultMessage="Sign out"
                description="Sign out button"
              />
            </span>
          )}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
